import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
  createCommand,
} from 'lexical';
import { PhxInsertNode } from './PhxInsertNode';
import { PhxDeleteNode } from './PhxDeleteNode';

export const CHANGE_FONT_COLOR_COMMAND = createCommand<string>();
export const CHANGE_BACKGROUND_COLOR_COMMAND = createCommand<string>();

export class PhxTextNode extends TextNode {
  __username?: string;
  __createdAt?: string;
  __id?: string;

  constructor(text: string, key?: NodeKey, username?: string, createdAt?: string, id?: string) {
    super(text, key);
    this.__username = username;
    this.__createdAt = createdAt;
    this.__id = id;
  }

  static getType(): string {
    return 'phx-text';
  }

  static clone(node: PhxTextNode): PhxTextNode {
    return new PhxTextNode(node.__text, node.__key, node.__username, node.__createdAt, node.__id);
  }

  isSimpleText(): boolean {
    return this.__type === 'phx-text' && this.__mode === 0;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('span');
    dom.textContent = this.__text;

    if (this.__username) {
      dom.setAttribute('data-username', this.__username);
    }

    if (this.__createdAt) {
      dom.setAttribute('data-created-at', this.__createdAt);
    }

    if (this.__id) {
      dom.setAttribute('id', this.__id);
    }

    const style = this.getStyle();
    if (style) {
      dom.setAttribute('style', style);
    }

    if (this.hasFormat('bold')) {
      dom.classList.add('PhxEditorTheme__textBold');
    }
    if (this.hasFormat('italic')) {
      dom.classList.add('PhxEditorTheme__textItalic');
    }
    if (this.hasFormat('underline')) {
      dom.classList.add('PhxEditorTheme__textUnderline');
    }
    if (this.hasFormat('strikethrough')) {
      dom.classList.add('PhxEditorTheme__textStrikethrough');
    }
    if (this.hasFormat('subscript')) {
      dom.classList.add('PhxEditorTheme__textSubscript');
    }
    if (this.hasFormat('superscript')) {
      dom.classList.add('PhxEditorTheme__textSuperscript');
    }
    if (this.hasFormat('code')) {
      dom.classList.add('PhxEditorTheme__textCode');
    }

    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const update = super.updateDOM(prevNode, dom, config);
    if (update) {
      const style = this.getStyle();
      if (style) {
        dom.setAttribute('style', style);
      }

      dom.classList.remove(
        'PhxEditorTheme__textBold',
        'PhxEditorTheme__textItalic',
        'PhxEditorTheme__textUnderline',
        'PhxEditorTheme__textStrikethrough',
        'PhxEditorTheme__textSubscript',
        'PhxEditorTheme__textSuperscript',
        'PhxEditorTheme__textCode'
      );

      if (this.hasFormat('bold')) {
        dom.classList.add('PhxEditorTheme__textBold');
      }
      if (this.hasFormat('italic')) {
        dom.classList.add('PhxEditorTheme__textItalic');
      }
      if (this.hasFormat('underline')) {
        dom.classList.add('PhxEditorTheme__textUnderline');
      }
      if (this.hasFormat('strikethrough')) {
        dom.classList.add('PhxEditorTheme__textStrikethrough');
      }
      if (this.hasFormat('subscript')) {
        dom.classList.add('PhxEditorTheme__textSubscript');
      }
      if (this.hasFormat('superscript')) {
        dom.classList.add('PhxEditorTheme__textSuperscript');
      }
      if (this.hasFormat('code')) {
        dom.classList.add('PhxEditorTheme__textCode');
      }
    }
    return update;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const spanElement = document.createElement('span');
    spanElement.textContent = this.__text;

    const style = this.getStyle();
    if (style) {
      spanElement.setAttribute('style', style);
    }

    if (this.hasFormat('bold')) {
      spanElement.classList.add('PhxEditorTheme__textBold');
    }
    if (this.hasFormat('italic')) {
      spanElement.classList.add('PhxEditorTheme__textItalic');
    }
    if (this.hasFormat('underline')) {
      spanElement.classList.add('PhxEditorTheme__textUnderline');
    }
    if (this.hasFormat('strikethrough')) {
      spanElement.classList.add('PhxEditorTheme__textStrikethrough');
    }
    if (this.hasFormat('subscript')) {
      spanElement.classList.add('PhxEditorTheme__textSubscript');
    }
    if (this.hasFormat('superscript')) {
      spanElement.classList.add('PhxEditorTheme__textSuperscript');
    }
    if (this.hasFormat('code')) {
      spanElement.classList.add('PhxEditorTheme__textCode');
    }

    return { element: spanElement };
  }

  static importJSON(serializedNode: SerializedTextNode): PhxTextNode {
    const node = super.importJSON(serializedNode);
    return node;
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'phx-text',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        return {
          conversion: convertPhxTextElement,
          priority: 1,
        };
      },
      strong: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('bold');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      b: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('bold');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      em: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('italic');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      i: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('italic');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      u: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('underline');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      s: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('strikethrough');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      sub: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('subscript');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      sup: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('superscript');
            return { node: textNode };
          },
          priority: 1,
        };
      },
      code: (domNode: HTMLElement) => {
        return {
          conversion: (node: HTMLElement) => {
            const textNode = $createPhxTextNode(node.textContent || '');
            textNode.toggleFormat('code');
            return { node: textNode };
          },
          priority: 1,
        };
      },
    };
  }
}

// Helper function for DOM conversion
function convertPhxTextElement(domNode: HTMLElement): DOMConversionOutput {
  const textContent = domNode.textContent || '';
  const style = domNode.getAttribute('style') || '';
  const className = domNode.getAttribute('class') || '';

  const isInsertNode =
    domNode.classList.contains('editor-insert-disabled') || domNode.classList.contains('editor-insert');
  const isDeleteNode =
    domNode.classList.contains('editor-delete-disabled') || domNode.classList.contains('editor-delete');

  const username = domNode.getAttribute('data-username') || '';
  const createdAt = domNode.getAttribute('data-created-at') || undefined;
  const id = domNode.getAttribute('id') || undefined;

  const node = (() => {
    if (isInsertNode) {
      const insertNode = new PhxInsertNode(username, id, createdAt);
      return $applyNodeReplacement(insertNode);
    } else if (isDeleteNode) {
      const deleteNode = new PhxDeleteNode(username, id, createdAt);
      return $applyNodeReplacement(deleteNode);
    } else {
      return $createPhxTextNode(textContent);
    }
  })();

  node.setStyle(style);

  // Handle PhxEditorTheme classes to restore text formatting
  if (className && node instanceof PhxTextNode) {
    const classes = className.split(' ');

    // Apply text formatting based on PhxEditorTheme classes
    if (classes.includes('PhxEditorTheme__textBold')) {
      node.toggleFormat('bold');
    }
    if (classes.includes('PhxEditorTheme__textItalic')) {
      node.toggleFormat('italic');
    }
    if (classes.includes('PhxEditorTheme__textUnderline')) {
      node.toggleFormat('underline');
    }
    if (classes.includes('PhxEditorTheme__textStrikethrough')) {
      node.toggleFormat('strikethrough');
    }
    if (classes.includes('PhxEditorTheme__textSubscript')) {
      node.toggleFormat('subscript');
    }
    if (classes.includes('PhxEditorTheme__textSuperscript')) {
      node.toggleFormat('superscript');
    }
    if (classes.includes('PhxEditorTheme__textCode')) {
      node.toggleFormat('code');
    }
  }

  return { node };
}

export function $createPhxTextNode(text: string): PhxTextNode {
  return $applyNodeReplacement(new PhxTextNode(text));
}

export function $isPhxTextNode(node: LexicalNode | null | undefined): node is PhxTextNode {
  return node instanceof PhxTextNode;
}
